import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { FieldArray, FormikBag, FormikProps, withFormik } from 'formik';
import { isArray } from 'lodash';
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import * as yup from 'yup';
import { getGroups, getUsers } from '../AppShell/Authentication/AuthorisationAsync';
import {
    GroupIGroup,
    GroupIGroupMember,
    GroupIParentGroup,
    GroupMemberType,
    IInvalidGroup,
    UserIUser
} from '../AppShell/Authentication/AuthorisationModels';
import { requiredField } from '../Authorisation.en';
import { Button, ButtonMode } from '../Components/Button';
import { StyledDialogActions } from '../Components/Dialog/StyledDialogActions';
import { StyledDialogButton } from '../Components/Dialog/StyledDialogButton';
import { StyledDialogContent } from '../Components/Dialog/StyledDialogContent';
import { FormikTextField } from '../Components/FormikFields/FormikTextField';
import { getErrorText } from '../Components/FormikFields/getErrorText';
import { DeleteIcon } from '../Components/Icons';
import { FormGrid } from '../Components/Layout/FormGrid';
import { AutoCompleteTypes } from '../Components/MaskedTextField';
import { StyledNavigationPrompt } from '../Components/Prompts/StyledNavigationPrompt';
import { StyledPrompt } from '../Components/Prompts/StyledPrompt';
import { SimpleDataTable } from '../Components/SimpleDataTable/SimpleDataTable';
import { StyledChip } from '../Components/StyledChip';
import { StyledSelect } from '../Components/StyledSelect';
import { SectionTitle } from '../Components/Typography/SectionTitle';
import { defaultAuthoringSystem, selfAuthoringSystemForeignKey } from '../Shared/Constants';
import { CancelledRequest, HttpExecutorConfig } from '../Shared/HttpRequestExecutor';
import { deepGetFirstNumberOrString } from '../Shared/ObjectUtilities';
import { lookupIdentifierToKey, lookupIdentifierToName } from '../Shared/SharedModels';
import { standardName } from '../Shared/StringUtilities';
import { iconLight, divider } from '../Styles/Colours';

import styles from './GroupFileInformation.module.scss';
import { SaveGroupInfoResults } from './GroupFileStore';

type GroupFileInformationProps = RouteComponentProps<{ id: string }> & {
    isNew?: boolean;
    isDeleting: boolean;
    group?: GroupIGroup;
    defaultGroup?: GroupIGroup;
    loadInformation(): Promise<void>;
    onError(error: string): void;
    onSave(group: GroupIGroup): Promise<SaveGroupInfoResults>;
    onClose(): void;
};

type GroupFileInformationFormProps = GroupFileInformationProps & FormikProps<GroupFileInformationFormState>;

export type GroupFileInformationFormState = GroupIGroup;

class ConfirmDefaultGroupActions extends React.Component<{ cancel: () => void; save: () => void }> {
    public render() {
        return (
            <>
                <StyledDialogButton
                    mode={ButtonMode.border}
                    borderColour={divider}
                    testId='cancel'
                    key='default-close'
                    onClick={this.props.cancel}
                >
                    Cancel
                </StyledDialogButton>
                <StyledDialogButton testId='proceed' key='default-save' onClick={this.props.save}>
                    Proceed
                </StyledDialogButton>
            </>
        );
    }
}

const validationSchema = yup.object().shape({
    username: yup.string().required(requiredField),
    name: yup.string().required(requiredField),
    enabled: yup.boolean(),
    isDefaultGroup: yup.boolean(),
    parentGroups: yup.array(),
    members: yup.array(),
    authoringSystem: yup.string().required(requiredField),
    authoringSystemForeignKey: yup.string().required(requiredField)
});

const mapPropsToValues = (props: GroupFileInformationProps): GroupFileInformationFormState => {
    if (props.group)
        return {
            id: props.group.id,
            username: props.group.username || '',
            name: props.group.name || '',
            enabled: props.group.enabled,
            isDefaultGroup: props.group.isDefaultGroup,
            parentGroups: props.group.parentGroups || [],
            members: props.group.members || [],
            authoringSystem: props.group.authoringSystem || defaultAuthoringSystem,
            authoringSystemForeignKey: props.group.authoringSystemForeignKey || selfAuthoringSystemForeignKey,
            createdBy: props.group.createdBy,
            createdOn: props.group.createdOn,
            updatedBy: props.group.updatedBy,
            updatedOn: props.group.updatedOn
        };

    return {
        id: '',
        username: '',
        name: '',
        enabled: true,
        isDefaultGroup: false,
        parentGroups: [],
        members: [],
        authoringSystem: defaultAuthoringSystem,
        authoringSystemForeignKey: selfAuthoringSystemForeignKey,
        createdBy: '',
        createdOn: '',
        updatedBy: '',
        updatedOn: ''
    };
};

const handleSubmit = async (
    values: GroupFileInformationFormState,
    formikBag: FormikBag<GroupFileInformationProps, GroupFileInformationFormState>
) => {
    const results: SaveGroupInfoResults = await formikBag.props.onSave(values);
    formikBag.setSubmitting(false);

    if (results.info === true && results.parents === true && results.members === true) {
        formikBag.resetForm(values);
        formikBag.setStatus([]);
        formikBag.props.onClose();
    } else {
        if (isArray(results.parents) && results.parents.length) {
            formikBag.setFieldError(
                'parentGroups',
                'Highlighted parent groups cause circular relationships with current child groups.'
            );
            formikBag.props.onError(
                'Highlighted parent groups cause circular relationships with current child groups.'
            );
        }

        if (isArray(results.members) && results.members.length) {
            formikBag.setFieldError(
                'members',
                'Highlighted child groups cause circular relationships with current parent groups.'
            );
            formikBag.props.onError(
                'Highlighted child groups cause circular relationships with current parent groups.'
            );
        }

        formikBag.setStatus([
            ...(isArray(results.parents) ? results.parents : []),
            ...(isArray(results.members) ? results.members : [])
        ]);
    }
};

class GroupFileInformationForm extends React.Component<
    GroupFileInformationFormProps,
    {
        confirm: boolean;
    }
> {
    public state = {
        confirm: false
    };

    public componentWillUnmount() {
        this.props.onError('');
    }

    public submit = () => {
        this.setState(
            {
                confirm: false
            },
            () => {
                this.validateAndSubmit();
            }
        );
    };

    public submitAndCheckForDefaultGroup = async () => {
        const confirmAsSystemContact =
            (this.props.values.isDefaultGroup && this.props.defaultGroup && this.props.isNew) ||
            (this.props.values.isDefaultGroup &&
                this.props.defaultGroup &&
                this.props.group &&
                this.props.defaultGroup.id !== this.props.group.id) ||
            false;

        this.props.setSubmitting(true);

        if (confirmAsSystemContact) {
            this.setState({ confirm: confirmAsSystemContact });
        } else {
            this.validateAndSubmit();
        }
    };

    public validateAndSubmit = () => {
        this.props.validateForm();
        const flatErrors = deepGetFirstNumberOrString(this.props.errors);
        this.props.onError(flatErrors.toString());
        this.props.submitForm();
    };

    public onCancelDefaultGroup = () => {
        this.setState({ confirm: false });
        this.props.setSubmitting(false);
    };

    public searchForParentGroups = async (
        config: HttpExecutorConfig,
        value: string,
        limit: number
    ): Promise<GroupIParentGroup[] | CancelledRequest> => {
        const propsGroup = this.props.group;
        let parentGroups: GroupIGroup[];
        if (!this.props.isNew && !!propsGroup) {
            let newParentGroups = await getGroups(config, value, true, limit + 1);
            if (newParentGroups instanceof CancelledRequest) return newParentGroups;

            parentGroups = newParentGroups.filter(group => group.id !== propsGroup.id);
        } else {
            let newParentGroups = await getGroups(config, value, true, limit);
            if (newParentGroups instanceof CancelledRequest) return newParentGroups;

            parentGroups = newParentGroups;
        }

        return parentGroups;
    };

    public userToGroupMember = (user: UserIUser): GroupIGroupMember => {
        const memberUser: GroupIGroupMember = {
            id: user.id,
            familyName: user.familyName,
            givenNames: user.givenNames,
            username: user.username,
            type: GroupMemberType.User,
            authoringSystem: user.authoringSystem,
            authoringSystemForeignKey: user.authoringSystemForeignKey
        };

        return memberUser;
    };

    public searchForMemberUsers = async (
        config: HttpExecutorConfig,
        value: string,
        limit: number
    ): Promise<GroupIGroupMember[] | CancelledRequest> => {
        const users = await getUsers(config, value, true, limit);
        if (users instanceof CancelledRequest) return users;

        return users.map(u => this.userToGroupMember(u));
    };

    public memberUserItemToString = (memberUser: GroupIGroupMember): string => {
        if (!!memberUser)
            return standardName({
                givenNames: !!memberUser.givenNames ? memberUser.givenNames : '',
                familyName: !!memberUser.familyName ? memberUser.familyName : ''
            });

        return '';
    };

    public groupToGroupMember = (group: GroupIGroup): GroupIGroupMember => {
        const groupMember: GroupIGroupMember = {
            id: group.id,
            name: group.name,
            username: group.username,
            type: GroupMemberType.Group,
            authoringSystem: group.authoringSystem,
            authoringSystemForeignKey: group.authoringSystemForeignKey
        };

        return groupMember;
    };

    public searchForMemberGroups = async (
        config: HttpExecutorConfig,
        value: string,
        limit: number
    ): Promise<GroupIGroupMember[] | CancelledRequest> => {
        const propsGroup = this.props.group;
        let memberGroups: GroupIGroup[];
        if (!this.props.isNew && !!propsGroup) {
            let newGroups = await getGroups(config, value, true, limit + 1);
            if (newGroups instanceof CancelledRequest) return newGroups;

            memberGroups = newGroups.filter(group => group.id !== propsGroup.id);
        } else {
            let newGroups = await getGroups(config, value, true, limit);
            if (newGroups instanceof CancelledRequest) return newGroups;

            memberGroups = newGroups;
        }

        return memberGroups.map(mg => this.groupToGroupMember(mg));
    };

    public onMemberUserSelection = (memberUsers: GroupIGroupMember[]) => {
        if (!!memberUsers) {
            this.props.setFieldTouched('members');
            this.props.setFieldValue('members', memberUsers);
        }
    };

    public onParentGroupSelection = (parentGroups: GroupIParentGroup[]) => {
        if (!!parentGroups) {
            this.props.setFieldTouched('parentGroups');
            this.props.setFieldValue('parentGroups', parentGroups);
        }
    };

    public onMemberGroupSelection = (memberGroups: GroupIGroupMember[]) => {
        if (!!memberGroups) {
            this.props.setFieldTouched('members');
            this.props.setFieldValue('members', memberGroups);
        }
    };

    public removeMemberGroupMember = (idToRemove: string) => {
        this.props.setFieldTouched('members');
        this.props.setFieldValue(
            'members',
            this.props.values.members.filter(member => member.id !== idToRemove)
        );
    };

    public removeParentGroupMember = (idToRemove: string) => {
        this.props.setFieldTouched('parentGroups');
        this.props.setFieldValue(
            'parentGroups',
            this.props.values.parentGroups.filter(parent => parent.id !== idToRemove)
        );
    };

    public onParentGroupClear = () => {
        this.props.setFieldTouched('parentGroups');
        this.props.setFieldValue('parentGroups', []);
    };

    public onMemberGroupClear = () => {
        const memberGroupsRemoved = this.props.values.members.filter(m => m.type !== GroupMemberType.Group);
        this.props.setFieldTouched('members');
        this.props.setFieldValue('members', memberGroupsRemoved);
    };

    public onMemberUserClear = () => {
        const memberUsersRemoved = this.props.values.members.filter(m => m.type !== GroupMemberType.User);
        this.props.setFieldTouched('members');
        this.props.setFieldValue('members', memberUsersRemoved);
    };

    public render() {
        const props = this.props;
        const status = props.status || [];

        const actions = (
            <>
                <StyledDialogButton
                    borderColour={divider}
                    testId='cancel'
                    mode={ButtonMode.border}
                    onClick={props.onClose}
                >
                    Cancel
                </StyledDialogButton>
                <StyledDialogButton
                    testId='save'
                    disabled={props.isSubmitting}
                    onClick={this.submitAndCheckForDefaultGroup}
                >
                    Save
                </StyledDialogButton>
            </>
        );

        const childGroups = props.values.members.filter(m => m.type === GroupMemberType.Group);
        const userMembers = props.values.members.filter(m => m.type === GroupMemberType.User);

        return (
            <>
                <StyledPrompt
                    actions={<ConfirmDefaultGroupActions save={this.submit} cancel={this.onCancelDefaultGroup} />}
                    open={this.state.confirm}
                >
                    {`You have already set ${
                        !this.props.defaultGroup ? '' : this.props.defaultGroup.name
                    } as the default group. If you proceed, that group will no longer be the default.`}
                </StyledPrompt>
                <StyledDialogContent noPadding>
                    <div className={styles.container}>
                        <FormGrid row>
                            <FormGrid half>
                                <SectionTitle title='BASIC DETAILS' />
                                <FormikTextField required name='username' label='Name' testId='username' />
                                <FormikTextField
                                    required
                                    name='name'
                                    label='Group name'
                                    autoCompleteType={AutoCompleteTypes.UserInfo}
                                    testId='name'
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={props.values.enabled}
                                            onChange={(event, checked) => {
                                                props.setFieldTouched('enabled');
                                                props.setFieldValue('enabled', checked);
                                            }}
                                        />
                                    }
                                    label='Enabled'
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={props.values.isDefaultGroup}
                                            onChange={(event, checked) => {
                                                props.setFieldTouched('isDefaultGroup');
                                                props.setFieldValue('isDefaultGroup', checked);
                                            }}
                                        />
                                    }
                                    label='Set as default group'
                                />
                                <FieldArray
                                    name='members'
                                    render={(membersHelpers: any) => {
                                        return (
                                            <>
                                                <SectionTitle title='USERS IN THIS GROUP' />
                                                <StyledSelect<GroupIGroupMember>
                                                    label='Add User'
                                                    testId='add-user'
                                                    maxResults={5}
                                                    value={userMembers}
                                                    fetch={this.searchForMemberUsers}
                                                    itemToString={this.memberUserItemToString}
                                                    itemToMenuKey={lookupIdentifierToKey}
                                                    isMulti
                                                    isClearable
                                                    hideMultiChips
                                                    onSelection={this.onMemberUserSelection}
                                                    onClear={this.onMemberUserClear}
                                                />
                                                <div
                                                    className={
                                                        userMembers && userMembers.length > 0
                                                            ? styles.dataTableContainer
                                                            : styles.inactive
                                                    }
                                                >
                                                    <SimpleDataTable<GroupIGroupMember>
                                                        data={userMembers}
                                                        enableScrollTo
                                                        disableHeader
                                                        reduceRowMargins
                                                        columns={[
                                                            {
                                                                key: 'name',
                                                                title: 'User name',
                                                                render: (row: GroupIGroupMember) =>
                                                                    standardName({
                                                                        givenNames: row.givenNames || '',
                                                                        familyName: row.familyName || ''
                                                                    }) ||
                                                                    row.name ||
                                                                    ''
                                                            },
                                                            {
                                                                key: 'id',
                                                                title: '',
                                                                width: 50,
                                                                render: (row: GroupIGroupMember) => (
                                                                    <Button
                                                                        aria-label='Delete'
                                                                        mode={ButtonMode.text}
                                                                        testId='delete'
                                                                        noShadowOnHover
                                                                        iconOnly
                                                                        onClick={() => {
                                                                            this.removeMemberGroupMember(row.id);
                                                                        }}
                                                                    >
                                                                        <DeleteIcon color={iconLight} />
                                                                    </Button>
                                                                )
                                                            }
                                                        ]}
                                                        selectedRows={[]}
                                                    />
                                                </div>
                                            </>
                                        );
                                    }}
                                />
                            </FormGrid>
                            <FormGrid half>
                                <SectionTitle title='INHERIT PERMISSIONS FROM' />
                                <FieldArray
                                    name='parentGroups'
                                    render={(parentGroupsHelpers: any) => {
                                        return (
                                            <>
                                                <StyledSelect<GroupIParentGroup>
                                                    label='Add Group'
                                                    testId='add-group'
                                                    maxResults={5}
                                                    value={props.values.parentGroups}
                                                    fetch={this.searchForParentGroups}
                                                    itemToString={lookupIdentifierToName}
                                                    itemToMenuKey={lookupIdentifierToKey}
                                                    errorText={getErrorText(props, 'parentGroups')}
                                                    isMulti
                                                    isClearable
                                                    hideMultiChips
                                                    onSelection={this.onParentGroupSelection}
                                                    onClear={this.onParentGroupClear}
                                                />

                                                {props.values.parentGroups.map(
                                                    (group: GroupIParentGroup, index: number) => {
                                                        const isInvalidGroup = status.find(
                                                            (g: IInvalidGroup) => g.id === group.id
                                                        );
                                                        return (
                                                            <StyledChip
                                                                key={group.id}
                                                                hasError={!!isInvalidGroup}
                                                                onRemove={() => this.removeParentGroupMember(group.id)}
                                                                hasAccent
                                                            >
                                                                {group.name}
                                                            </StyledChip>
                                                        );
                                                    }
                                                )}
                                            </>
                                        );
                                    }}
                                />
                                <SectionTitle title='DISTRIBUTE PERMISSIONS TO' />
                                <FieldArray
                                    name='members'
                                    render={(membersHelpers: any) => {
                                        return (
                                            <>
                                                <StyledSelect<GroupIGroupMember>
                                                    label='Add Group'
                                                    testId='add-group'
                                                    maxResults={5}
                                                    value={childGroups}
                                                    fetch={this.searchForMemberGroups}
                                                    itemToString={lookupIdentifierToName}
                                                    itemToMenuKey={lookupIdentifierToKey}
                                                    errorText={getErrorText(props, 'members')}
                                                    isMulti
                                                    isClearable
                                                    hideMultiChips
                                                    onSelection={this.onMemberGroupSelection}
                                                    onClear={this.onMemberGroupClear}
                                                />

                                                {childGroups.map((group: GroupIGroupMember, index) => {
                                                    const isInvalidGroup = status.find(
                                                        (g: IInvalidGroup) => g.id === group.id
                                                    );
                                                    return (
                                                        <StyledChip
                                                            key={group.id}
                                                            hasError={!!isInvalidGroup}
                                                            onRemove={() => this.removeMemberGroupMember(group.id)}
                                                            hasAccent
                                                        >
                                                            {group.name}
                                                        </StyledChip>
                                                    );
                                                })}
                                            </>
                                        );
                                    }}
                                />
                            </FormGrid>
                        </FormGrid>
                    </div>
                </StyledDialogContent>
                <StyledNavigationPrompt
                    when={!props.isDeleting && props.dirty}
                    onSave={this.submitAndCheckForDefaultGroup}
                />
                <StyledDialogActions>{actions}</StyledDialogActions>
            </>
        );
    }
}

export const GroupFileInformation = withFormik<GroupFileInformationProps, GroupFileInformationFormState>({
    handleSubmit,
    mapPropsToValues,
    validationSchema
})(GroupFileInformationForm);
